import ExpenseFormItem from "../../components/expense-form-item";
import AutoCompleteField from "../../components/field-auto-complete";
import { expandMatterNumber, formatMatterLabelFromData, formatMatterNumber } from "../../utils/matter-display";
import Switch from "react-switch";

const NewExpenseDetailsModal = ({
  data,
  expense,
  travelTypes,        // provided for mileage flow; absent for receipt flow
  travelTypesLoading, // true while GET /expense_rates/ is in flight
  travelTypesError,   // true when the travel types fetch failed
  offlineVersion,
  updateExpenseField,
  hasNext,
  onNext,
  hasPrevious,
  onPrevious,
  onFinish,
  onCancel,
}) => {
  // When travelTypes is supplied this is the mileage Step 1 flow.
  // When absent, it is the legacy receipt upload flow (billable toggle).
  const isMileageFlow = travelTypes !== undefined;
  const selectedTravelType = isMileageFlow
    ? (travelTypes.find(tt => tt.id === expense.travelTypeId) ?? null)
    : null;
  const isBillable = isMileageFlow
    ? selectedTravelType?.billable_scope === "billable"
    : expense.isBillable;

  const isNextStepDisabled = isMileageFlow
    ? !expense.travelTypeId || !expense.description || (isBillable && !expense.matterId)
    : !expense.description || (expense.isBillable && !expense.matterId);

  // Shared matter ID field — position differs per flow but content is identical.
  const matterField = (
    <ExpenseFormItem data={data} label={data.t.read("invoice.matter_id")} show={isBillable} alwaysUseMobileStyling={true}>
      <AutoCompleteField
        value={expense.matterId || ""}
        onChange={val => updateExpenseField("matterId", val.name ?? val)}
        className="form-control"
        searchUrl="/matters/search/"
        // XPEN-3322: the picker edits the collapsed number; the full one is
        // restored before it is stored, so MatterId still resolves at export.
        formatEditValue={formatMatterNumber}
        parseEditValue={expandMatterNumber}
        minLength={1}
        max_results={6}
        dropup={true}
        placeholder={data.t.read("invoice.matter_id")}
        data-testid="expense-matter-id"
        formatLabel={(m) => formatMatterLabelFromData(data, m)}
      />
    </ExpenseFormItem>
  );

  return (
    <div className="new-expense-details-modal modal vh-100 w-100 d-flex flex-column" data-testid="new-expense-details-modal">
      {expense.file && (
        <div className="bg-secondary w-100 h-50 border-bottom overflow-hidden d-flex justify-content-center align-items-center">
          <img src={URL.createObjectURL(expense.file)} alt="Expense" className="mw-100 mh-100" />
        </div>
      )}

      <div className="bg-light py-3 flex-1 d-flex flex-column justify-content-center align-items-center">
        <div>
          {/* Travel type picker — mileage flow only */}
          {isMileageFlow && (
            <ExpenseFormItem data={data} label={data.t.read("mileage_step1_travel_type") || "Travel Type"} show={true} alwaysUseMobileStyling={true}>
              {travelTypesError ? (
                <p className="text-danger small">{data.t.read("failed_to_fetch_rates") || "Failed to load travel types. Please refresh."}</p>
              ) : travelTypesLoading ? (
                <p className="text-muted small">…</p>
              ) : travelTypes.length === 0 ? (
                <p className="text-muted small">{data.t.read("mileage_step1_no_travel_types") || "No active travel types configured."}</p>
              ) : (
                <select
                  className="form-control"
                  value={expense.travelTypeId || ""}
                  onChange={(e) => updateExpenseField("travelTypeId", e.target.value)}
                  data-testid="travel-type-select"
                >
                  <option value="">{data.t.read("mileage_step1_select_travel_type") || "— Select a travel type —"}</option>
                  {travelTypes.map(tt => (
                    <option key={tt.id} value={tt.id}>{tt.name}</option>
                  ))}
                </select>
              )}
            </ExpenseFormItem>
          )}

          {/* Matter ID — mileage flow: below Travel Type, above Description */}
          {isMileageFlow && matterField}

          <ExpenseFormItem data={data} label={data.t.read("add_description")} show={true} alwaysUseMobileStyling={true}>
            <input
              type="text"
              value={expense.description}
              onChange={(e) => updateExpenseField("description", e.target.value)}
              autoFocus
              className="form-control"
              placeholder={data.t.read("invoice.description")}
              data-testid="expense-description"
            />
          </ExpenseFormItem>

          {/* Billable toggle — receipt flow only */}
          {!isMileageFlow && (
            <ExpenseFormItem data={data} label={data.t.read("add_billable")} show={true} alwaysUseMobileStyling={true}>
              <Switch
                checked={expense.isBillable}
                width={70}
                onColor="#F39800"
                boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                onChange={val => updateExpenseField("isBillable", val)}
                data-testid="expense-billable"
              />
            </ExpenseFormItem>
          )}

          {/* Matter ID — receipt flow: below Billable toggle */}
          {!isMileageFlow && matterField}
        </div>

        <button
          className="btn btn-primary btn-cta mt-3 mx-auto px-4"
          disabled={isNextStepDisabled}
          onClick={hasNext ? onNext : onFinish}
          data-testid="next-or-finish"
        >
          {isMileageFlow ? data.t.read('mileage_step1_save') || 'Save' : data.t.read(hasNext ? "next_page" : "done")}
        </button>
        <button
          className="btn btn-thin mt-2 mx-auto px-4"
          onClick={hasPrevious ? onPrevious : onCancel}
          data-testid="previous-or-cancel"
        >
          {hasPrevious ? data.t.read("previous_page") : data.t.read("cancel")}
        </button>
      </div>
    </div>
  );
};

export default NewExpenseDetailsModal;
